import React, { createContext, useContext, useEffect, useState, useCallback, useMemo } from 'react';
import { exchangeRates as exchangeRatesApi } from '@/lib/api';

const CurrencyContext = createContext(null);

const RATES_CACHE_KEY = 'tem_exchange_rates_v1';
const RATES_CACHE_TTL_MS = 60 * 60 * 1000; // 1 saat

// Ülke koduna göre para birimi mapping
const countryCurrencyMap = {
  'TR': 'TRY',
  'US': 'USD',
  'GB': 'GBP',
  'DE': 'EUR',
  'FR': 'EUR',
  'ES': 'EUR',
  'IT': 'EUR',
  'NL': 'EUR',
  'BE': 'EUR',
  'PT': 'EUR',
  'GR': 'EUR',
  'CH': 'CHF',
  'LI': 'CHF',
  'JP': 'JPY',
  'CN': 'CNY',
  'IN': 'INR',
  'AU': 'AUD',
  'CA': 'CAD',
  'RU': 'RUB',
  'SA': 'SAR',
  'AE': 'AED',
  'BH': 'BHD',
  'KW': 'KWD',
  'QA': 'QAR',
  'IQ': 'IQD',
  'IR': 'IRR',
  'OM': 'OMR',
  'JO': 'JOD',
  'PK': 'PKR',
  'MY': 'MYR',
};

const currencySymbols = {
  'USD': '$',
  'EUR': '€',
  'GBP': '£',
  'TRY': '₺',
  'CHF': 'Fr.',
  'JPY': '¥',
  'CNY': '¥',
  'INR': '₹',
  'AUD': 'A$',
  'CAD': 'C$',
  'RUB': '₽',
  'SAR': 'SR',
  'AED': 'AED',
  'BHD': 'BD',
  'KWD': 'KD',
  'QAR': 'QR',
  'IQD': 'IQD',
  'IRR': 'IRR',
  'OMR': 'OMR',
  'JOD': 'JD',
  'PKR': 'Rs',
  'MYR': 'RM',
};

/** API erişilemezse yedek (1 TRY = X birim) */
const FALLBACK_RATES_FROM_TRY = {
  'TRY': 1,
  'USD': 0.029,
  'EUR': 0.027,
  'GBP': 0.023,
  'CHF': 0.026,
  'JPY': 4.33,
  'CNY': 0.21,
  'INR': 2.41,
  'AUD': 0.044,
  'CAD': 0.039,
  'RUB': 2.68,
  'SAR': 0.109,
  'AED': 0.107,
  'BHD': 0.011,
  'KWD': 0.009,
  'QAR': 0.106,
  'IQD': 38.0,
  'IRR': 1220,
  'OMR': 0.011,
  'JOD': 0.021,
  'PKR': 8.06,
  'MYR': 0.13,
};

const MARKUP_RATE = 0;
const SUPPORTED_CURRENCIES = Object.keys(currencySymbols);

const readRatesCache = () => {
  try {
    const raw = localStorage.getItem(RATES_CACHE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (!parsed?.rates_from_try || !parsed?.saved_at) return null;
    if (Date.now() - parsed.saved_at > RATES_CACHE_TTL_MS) return null;
    return parsed;
  } catch {
    return null;
  }
};

const writeRatesCache = (payload) => {
  try {
    localStorage.setItem(RATES_CACHE_KEY, JSON.stringify({
      rates_from_try: payload.rates_from_try,
      source: payload.source,
      fetched_at: payload.fetched_at,
      saved_at: Date.now(),
    }));
  } catch {
    /* ignore quota errors */
  }
};

export const CurrencyProvider = ({ children }) => {
  const [currency, setCurrency] = useState('EUR');
  const [countryCode, setCountryCode] = useState('DE');
  const [loading, setLoading] = useState(true);
  const [ratesFromTry, setRatesFromTry] = useState(() => {
    const cached = readRatesCache();
    return cached?.rates_from_try || FALLBACK_RATES_FROM_TRY;
  });
  const [ratesMeta, setRatesMeta] = useState(() => {
    const cached = readRatesCache();
    return {
      source: cached?.source || 'fallback',
      fetchedAt: cached?.fetched_at || null,
      tryPerEur: cached?.rates_from_try?.EUR ? 1 / cached.rates_from_try.EUR : null,
    };
  });

  const loadExchangeRates = useCallback(async () => {
    try {
      const res = await exchangeRatesApi.get();
      const data = res.data || {};
      if (data.rates_from_try && typeof data.rates_from_try === 'object') {
        setRatesFromTry({ ...FALLBACK_RATES_FROM_TRY, ...data.rates_from_try, TRY: 1 });
        setRatesMeta({
          source: data.source || 'live',
          fetchedAt: data.fetched_at || null,
          tryPerEur: data.try_per_eur ?? null,
        });
        writeRatesCache(data);
      }
    } catch (error) {
      console.warn('Canlı döviz kurları alınamadı, önbellek/yedek kullanılıyor:', error);
    }
  }, []);

  useEffect(() => {
    loadExchangeRates();
  }, [loadExchangeRates]);

  useEffect(() => {
    const detectCurrency = async () => {
      try {
        const savedCurrency = localStorage.getItem('preferredCurrency');
        if (savedCurrency && SUPPORTED_CURRENCIES.includes(savedCurrency)) {
          setCurrency(savedCurrency);
          return;
        }

        const browserLang = navigator.language || navigator.userLanguage || 'en-US';
        const langParts = browserLang.split('-');
        let detectedCountry = 'DE';

        if (langParts.length > 1) {
          detectedCountry = langParts[1].toUpperCase();
        } else {
          const langToCountry = {
            'tr': 'TR', 'de': 'DE', 'fr': 'FR', 'es': 'ES', 'it': 'IT',
            'en': 'US', 'pt': 'PT', 'nl': 'NL', 'ru': 'RU', 'ar': 'SA',
            'zh': 'CN', 'ja': 'JP', 'fa': 'IR',
          };
          detectedCountry = langToCountry[langParts[0].toLowerCase()] || 'DE';
        }

        setCountryCode(detectedCountry);
        setCurrency(countryCurrencyMap[detectedCountry] || 'EUR');
      } catch (error) {
        console.error('Failed to detect currency:', error);
        setCurrency('EUR');
      } finally {
        setLoading(false);
      }
    };

    detectCurrency();
  }, []);

  const changeCurrency = useCallback((newCurrency) => {
    if (SUPPORTED_CURRENCIES.includes(newCurrency)) {
      setCurrency(newCurrency);
      localStorage.setItem('preferredCurrency', newCurrency);
    }
  }, []);

  const convertPrice = useCallback((tryPrice, sourceCurrency = 'TRY') => {
    if (!tryPrice) return 0;

    let priceInTRY = tryPrice;
    if (sourceCurrency !== 'TRY' && ratesFromTry[sourceCurrency]) {
      priceInTRY = tryPrice / ratesFromTry[sourceCurrency];
    }

    const rate = ratesFromTry[currency] ?? 1;
    const convertedPrice = priceInTRY * rate;
    return convertedPrice * (1 + MARKUP_RATE);
  }, [currency, ratesFromTry]);

  const formatPrice = useCallback((price, sourceCurrency = 'TRY') => {
    const convertedPrice = convertPrice(price, sourceCurrency);
    const symbol = currencySymbols[currency] || currency;

    if (currency === 'JPY') {
      return `${symbol}${Math.round(convertedPrice).toLocaleString('ja-JP')}`;
    }
    if (currency === 'TRY') {
      return `${convertedPrice.toLocaleString('tr-TR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} ${symbol}`;
    }
    if (currency === 'EUR') {
      return `${symbol}${convertedPrice.toLocaleString('de-DE', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
    }
    return `${symbol}${convertedPrice.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  }, [convertPrice, currency]);

  const value = useMemo(() => ({
    currency,
    countryCode,
    loading,
    changeCurrency,
    convertPrice,
    formatPrice,
    exchangeRates: ratesFromTry,
    currencySymbols,
    availableCurrencies: SUPPORTED_CURRENCIES,
    baseCurrency: 'TRY',
    ratesSource: ratesMeta.source,
    ratesFetchedAt: ratesMeta.fetchedAt,
    tryPerEur: ratesMeta.tryPerEur,
    refreshExchangeRates: loadExchangeRates,
  }), [
    currency,
    countryCode,
    loading,
    changeCurrency,
    convertPrice,
    formatPrice,
    ratesFromTry,
    ratesMeta,
    loadExchangeRates,
  ]);

  return (
    <CurrencyContext.Provider value={value}>
      {children}
    </CurrencyContext.Provider>
  );
};

export const useCurrency = () => {
  const context = useContext(CurrencyContext);
  if (!context) {
    throw new Error('useCurrency must be used within a CurrencyProvider');
  }
  return context;
};
